import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import { ApiError } from '@starling/core/api/errors';
import type { components } from '@/api/types.gen';

export type Order = components['schemas']['Order'];
export type OrderCreate = components['schemas']['OrderCreate'];
export type OrderUpdate = components['schemas']['OrderUpdate'];
export type OrderPage = components['schemas']['OrderPage'];
export type OrderStatusEvent = components['schemas']['OrderStatusEvent'];
export type OrderEventList = components['schemas']['OrderEventList'];
export type OrderStatus = components['schemas']['OrderStatus'];
export type Template = components['schemas']['Template'];

export interface OrdersQueryParams {
  page: number;
  pageSize: number;
  status?: OrderStatus;
  customerId?: string;
}

function buildOrdersUrl(params: OrdersQueryParams): string {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  if (params.status) sp.set('status', params.status);
  if (params.customerId) sp.set('customer_id', params.customerId);
  return `/api/v1/admin/orders?${sp.toString()}`;
}

export function useOrdersQuery(params: OrdersQueryParams) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => api.get<OrderPage>(buildOrdersUrl(params)),
    placeholderData: (prev) => prev,
  });
}

export function useOrderQuery(orderId: string | undefined) {
  return useQuery({
    enabled: Boolean(orderId),
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => api.get<Order>(`/api/v1/admin/orders/${orderId}`),
  });
}

export function useOrderEventsQuery(orderId: string | undefined) {
  return useQuery({
    enabled: Boolean(orderId),
    queryKey: ['orders', 'events', orderId],
    queryFn: () => api.get<OrderEventList>(`/api/v1/admin/orders/${orderId}/events`),
  });
}

// Suggested template: 404 — это нормальный кейс (нет mapping'а для статуса),
// не ретраим, не показываем как ошибку. Глобальный retry-config уже отсекает
// 4xx (см. App.tsx), плюс мы явно ловим 404 как «нет данных».
export function useSuggestedTemplateQuery(orderId: string | undefined) {
  return useQuery({
    enabled: Boolean(orderId),
    queryKey: ['orders', 'suggested-template', orderId],
    queryFn: async (): Promise<Template | null> => {
      try {
        return await api.get<Template>(`/api/v1/admin/orders/${orderId}/suggested-template`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useCreateOrderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['orders', 'create'],
    mutationFn: (input: OrderCreate) => api.post<Order>('/api/v1/admin/orders', input),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      qc.setQueryData(['orders', 'detail', created.id], created);
    },
  });
}

export interface UpdateOrderInput {
  id: string;
  patch: OrderUpdate;
}

export function useUpdateOrderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['orders', 'update'],
    mutationFn: ({ id, patch }: UpdateOrderInput) =>
      api.patch<Order>(`/api/v1/admin/orders/${id}`, patch),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['orders', 'events', updated.id] });
      qc.setQueryData(['orders', 'detail', updated.id], updated);
    },
  });
}
